import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SentimentJudgmentEmotionName } from 'services/callScribe/types';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

const LIGHT: Record<SentimentJudgmentEmotionName, string> = {
  Satisfied: '#2f9e62', Neutral: '#8b95a7', Confused: '#7a5fd0', Anxious: '#d79a1b', Frustrated: '#e06a2c', Angry: '#c8322f',
};
const DARK: Record<SentimentJudgmentEmotionName, string> = {
  Satisfied: '#4cc488', Neutral: '#9aa4b6', Confused: '#a58bff', Anxious: '#f0b53c', Frustrated: '#ff8a4f', Angry: '#ff6159',
};

export const emotionColor = (theme: Theme, emotion: SentimentJudgmentEmotionName): string =>
  (theme.palette.mode === 'dark' ? DARK : LIGHT)[emotion];

const useEmotionSpectrumStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      isolation: 'isolate',
      minWidth: 0,
    },
    story: {
      ...CS_FONT.small,
      margin: 0,
      fontWeight: CS_WEIGHT.heavy,
      color: t.text,
    },
    chart: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      minWidth: 0,
    },
    lane: {
      display: 'grid',
      gridTemplateColumns: '92px 1fr',
      alignItems: 'center',
      gap: 10,
      '@media (max-width: 560px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: 4,
      },
    },
    laneLabel: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.text2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    track: {
      position: 'relative',
      height: 28,
      borderRadius: 8,
      overflow: 'hidden',
      background: t.surface3,
    },
    segment: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      border: 0,
      padding: 0,
      margin: 0,
      transition: 'filter 120ms ease-out',
      '&:hover': {
        filter: 'brightness(1.12)',
      },
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: -2,
        zIndex: 1,
      },
    },
    clickable: {
      cursor: 'pointer',
    },
    carry: {
      pointerEvents: 'none',
    },
    flag: {
      position: 'absolute',
      top: -2,
      width: 0,
      height: 0,
      transform: 'translateX(-6px)',
      zIndex: 2,
      padding: 0,
      background: 'transparent',
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderTop: `9px solid ${t.text}`,
      borderBottom: 0,
      cursor: 'default',
      '&:focus-visible': {
        outline: `2px solid ${t.focus}`,
        outlineOffset: 2,
      },
    },
    axis: {
      position: 'relative',
      height: 16,
      marginLeft: 102,
      '@media (max-width: 560px)': {
        marginLeft: 0,
      },
    },
    tick: {
      position: 'absolute',
      top: 0,
      transform: 'translateX(-50%)',
      ...CS_FONT.caption,
      color: t.text2,
      fontVariantNumeric: 'tabular-nums',
    },
    tickEnd: {
      transform: 'translateX(-100%)',
    },
    tickStart: {
      transform: 'none',
    },
    summary: {
      ...CS_FONT.small,
      color: t.text,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      '& strong': {
        fontWeight: CS_WEIGHT.heavy,
      },
    },
    legend: {
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: 12,
      rowGap: 4,
      ...CS_FONT.caption,
      color: t.text2,
    },
    swatch: {
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: 3,
      marginRight: 6,
      verticalAlign: -1,
    },
    unavailable: {
      ...CS_FONT.small,
      color: t.text2,
    },
    empty: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '4px 0',
      minWidth: 0,
    },
    emptyRing: {
      flex: '0 0 auto',
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      background: t.surface3,
      color: t.text2,
      '& svg': {
        fontSize: 20,
      },
    },
    emptyText: {
      ...CS_FONT.small,
      margin: 0,
      color: t.text2,
      fontWeight: CS_WEIGHT.strong,
    },
  };
});

export default useEmotionSpectrumStyles;
